<template>
  <!-- Hero and Features Section (Version B)
       - 左侧文案区与整体背景层与 HeroFeatures.vue(A) 保持一致；
       - 右侧轮播区取消，改为「对比区」：上方两张对比图 + 4 个 gap 标注，
         下方为 AI 后期分析预览内嵌框（hover 展开浮层，交互与样式对齐上方 gap tag）。
         上下区域比例不固定，以完整展示上方对比图为准，下方预览框随内容自适应。
       gap 与 AI 分析数据来自 result subject 2d9be7a0 的 AI job 缓存。 -->
  <section
    id="features"
    class="hero-features-section min-h-screen relative overflow-hidden flex items-center"
  >
    <div class="hero-theme-overlay absolute inset-0 z-10"></div>
    <div class="hero-vignette absolute inset-0 z-20"></div>
    <div class="hero-film-grain absolute inset-0 z-20"></div>
    <div class="hero-guide-grid absolute inset-0 z-20"></div>
    <div class="absolute inset-0 z-30 pointer-events-none">
      <div
        class="hero-glow-orb-primary absolute top-1/4 left-1/4 w-[35rem] h-[35rem] rounded-full filter blur-3xl mix-blend-overlay transform -translate-x-1/2 -translate-y-1/2"
      ></div>
      <div class="hero-light-leak absolute inset-y-0 right-0 w-[42rem]"></div>
    </div>
    <div class="mx-auto w-full max-w-[1440px] px-4 lg:px-8 xl:px-12 2xl:px-16 hero-content-layer">
      <div class="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-10 xl:gap-14">
        <!-- Hero 左侧文案区：与 A 完全一致 -->
        <div class="hero-copy w-full lg:w-1/2 lg:max-w-[40rem] xl:max-w-[44rem] text-center lg:text-left px-2 py-6 lg:py-8">
          <p
            class="hero-kicker inline-flex items-center justify-center lg:justify-start gap-3 mb-5"
          >
            <span class="hero-kicker-mark" aria-hidden="true"></span>
            <span>PhotoScope</span>
          </p>
          <h1 class="hero-title mb-6">
            <span class="hero-title-line hero-title-line-primary">{{
              t("landing.hero.titleLine1")
            }}</span>
            <span class="hero-title-line hero-title-line-accent">{{
              t("landing.hero.titleLine2")
            }}</span>
          </h1>
          <p class="hero-subtitle hero-subtitle-text mb-10">
            {{ t("landing.hero.subtitle") }}
          </p>
          <button
            @click.prevent="navigateToUpload"
            class="theme-cta-primary theme-cta-primary--with-icon z-40"
          >
            <span class="theme-cta-primary__label">{{ $t("landing.startFree") }}</span>
            <span class="theme-cta-primary__icon" aria-hidden="true">
              <svg viewBox="0 0 20 20" fill="none">
                <path
                  d="M6.75 10h6.5"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />
                <path
                  d="M10.5 6.75 13.75 10l-3.25 3.25"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
          </button>
        </div>

        <!-- Hero 右侧对比区：复用独立组件 HeroCompareShowcase（variant=landing）。
             保留原 wrapper 的 w-full lg:flex-1 relative，约束右栏宽度不超过剩余空间，
             避免挤占左侧文案区；因图片为 3/4 比例，宽度受限后高度自然回到原尺寸。 -->
        <HeroCompareShowcase
          class="hero-b-compare-column w-full lg:flex-1 relative"
          variant="landing"
          @content-revealed="onShowcaseContentRevealed"
          @case-cta-clicked="onShowcaseCaseCtaClicked"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useLandingAnalytics } from "@/core/analytics/hooks/useLandingAnalytics";
import { useUserStore } from "@/core/store/useUserStore";
import HeroCompareShowcase from "./HeroCompareShowcase.vue";

const analytics = useLandingAnalytics();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();


const getLandingContext = () => {
  let isLoggedIn = false;
  try {
    isLoggedIn = Boolean(useUserStore().isLoggedIn);
  } catch {
    isLoggedIn = false;
  }

  return {
    lang: route?.params?.lang,
    is_logged_in: isLoggedIn,
  };
};

// HeroCompareShowcase 的埋点由父级接入：组件内仅 emit 稳定上下文字段，
// 此处用 useLandingAnalytics 原样上报，事件名/字段与重构前一致，零回归。
const onShowcaseContentRevealed = ({ elementName, contentId, contentType }) => {
  analytics.trackLandingContentClicked({
    ...getLandingContext(),
    event_label: "content_revealed",
    source_position: "landing_hero_b",
    element_name: elementName,
    element_type: "button",
    method: "hover",
    content_id: contentId,
    content_type: contentType,
  });
};

const onShowcaseCaseCtaClicked = (ctx) => {
  const startedAt = Date.now();
  analytics.trackLandingCtaClicked({ ...getLandingContext(), ...ctx });
  analytics.trackLandingCtaNavigationCompleted({
    ...getLandingContext(),
    ...ctx,
    action_status: "success",
    duration_ms: Date.now() - startedAt,
  });
};

const navigateToUpload = async () => {
  const startedAt = Date.now();
  analytics.trackLandingCtaClicked({
    ...getLandingContext(),
    source_position: "landing_hero",
    element_name: "upload_cta",
    cta_type: "upload",
    destination_type: "route",
    destination_id: "upload",
    content_id: "upload",
    content_type: "route",
  });

  try {
    await router.push({
      name: "upload",
      params: { lang: route.params.lang || "en" },
    });
    analytics.trackLandingCtaNavigationCompleted({
      ...getLandingContext(),
      source_position: "landing_hero",
      element_name: "upload_cta",
      element_type: "button",
      action_status: "success",
      cta_type: "upload",
      destination_type: "route",
      destination_id: "upload",
      content_id: "upload",
      content_type: "route",
      duration_ms: Date.now() - startedAt,
    });
  } catch {
    analytics.trackLandingCtaNavigationCompleted({
      ...getLandingContext(),
      source_position: "landing_hero",
      element_name: "upload_cta",
      element_type: "button",
      action_status: "failed",
      reason_code: "router_push_failed",
      cta_type: "upload",
      destination_type: "route",
      destination_id: "upload",
      content_id: "upload",
      content_type: "route",
      duration_ms: Date.now() - startedAt,
    });
  }
};
</script>

<style scoped>
/* ── 背景层与左侧文案区：与 HeroFeatures.vue(A) 保持一致 ── */
.hero-features-section {
  isolation: isolate;
  z-index: 0;
  background: radial-gradient(
      circle at 12% 18%,
      color-mix(in srgb, var(--theme-primary) 14%, transparent) 0%,
      transparent 28%
    ),
    radial-gradient(
      circle at 86% 14%,
      color-mix(in srgb, var(--theme-accent) 16%, transparent) 0%,
      transparent 24%
    ),
    linear-gradient(
      145deg,
      color-mix(in srgb, var(--zinc-950) 96%, var(--zinc-900)) 0%,
      color-mix(in srgb, var(--zinc-900) 94%, var(--zinc-800)) 52%,
      color-mix(in srgb, var(--zinc-950) 92%, var(--theme-primary-hover) 8%)
        100%
    );
}

.hero-content-layer {
  position: relative;
  z-index: 40;
}

.hero-theme-overlay {
  pointer-events: none;
  background-image: linear-gradient(
    135deg,
    color-mix(in srgb, var(--theme-primary-hover) 12%, transparent) 0%,
    transparent 34%,
    transparent 62%,
    color-mix(in srgb, var(--theme-accent) 12%, transparent) 100%
  );
  opacity: 0.85;
}

.hero-vignette {
  pointer-events: none;
  background: radial-gradient(
    ellipse at center,
    transparent 60%,
    color-mix(in srgb, var(--zinc-950) 7%, transparent) 70%,
    color-mix(in srgb, var(--zinc-950) 55%, transparent) 100%
  );
}

.hero-film-grain {
  pointer-events: none;
  opacity: 0.16;
  mix-blend-mode: soft-light;
  background-image: repeating-linear-gradient(
      0deg,
      color-mix(in srgb, var(--zinc-50) 7%, transparent) 0,
      color-mix(in srgb, var(--zinc-50) 7%, transparent) 1px,
      transparent 1px,
      transparent 3px
    ),
    repeating-linear-gradient(
      90deg,
      color-mix(in srgb, var(--zinc-50) 4%, transparent) 0,
      color-mix(in srgb, var(--zinc-50) 4%, transparent) 1px,
      transparent 1px,
      transparent 4px
    );
}

.hero-guide-grid {
  pointer-events: none;
  opacity: 0.14;
  background-image: linear-gradient(
      to right,
      transparent 0,
      transparent calc(50% - 1px),
      color-mix(in srgb, var(--zinc-50) 11%, transparent) 50%,
      transparent calc(50% + 1px),
      transparent 100%
    ),
    linear-gradient(
      to bottom,
      transparent 0,
      transparent calc(50% - 1px),
      color-mix(in srgb, var(--zinc-50) 8%, transparent) 50%,
      transparent calc(50% + 1px),
      transparent 100%
    );
}

.hero-glow-orb-primary {
  pointer-events: none;
  background-image: linear-gradient(
    to right,
    color-mix(in srgb, var(--theme-gradient-start) 14%, transparent),
    color-mix(in srgb, var(--theme-gradient-end) 10%, transparent)
  );
  opacity: 0.52;
}

.hero-light-leak {
  pointer-events: none;
  background: linear-gradient(
    90deg,
    transparent 0%,
    color-mix(in srgb, var(--theme-accent) 5%, transparent) 72%,
    color-mix(in srgb, var(--theme-primary) 12%, transparent) 82%,
    color-mix(in srgb, var(--theme-accent) 10%, transparent) 100%
  );
  filter: blur(24px);
  opacity: 0.22;
}

.hero-copy {
  max-width: 44rem;
}

.hero-kicker {
  margin: 0 0 1.25rem;
  font-family: "Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont,
    sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--zinc-100) 72%, var(--theme-accent));
}

.hero-kicker-mark {
  display: inline-block;
  width: 3rem;
  height: 1px;
  background: color-mix(in srgb, var(--theme-accent) 65%, var(--zinc-50));
}

.hero-title {
  display: flex;
  flex-direction: column;
  gap: 0.12em;
  margin: 0 0 1.5rem;
  font-family: "Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont,
    "Segoe UI", Roboto, sans-serif;
  font-size: clamp(2.75rem, 4vw, 5rem);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.03em;
}

.hero-title-line {
  display: block;
  text-wrap: balance;
}

.hero-title-line-primary {
  color: color-mix(in srgb, var(--zinc-50) 94%, var(--theme-primary) 6%);
}

.hero-title-line-accent {
  color: color-mix(in srgb, var(--zinc-100) 76%, var(--theme-accent) 24%);
}

.hero-subtitle {
  max-width: 38rem;
  font-family: "Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont,
    sans-serif;
  font-size: clamp(1rem, 1.6vw, 1.25rem);
  font-weight: 400;
  line-height: 1.65;
  letter-spacing: 0;
}

.hero-subtitle-text {
  color: color-mix(in srgb, var(--zinc-100) 82%, var(--zinc-400));
}

</style>
